<template>
    <div class="ppv2-flex ppv2-flex-col ppv2-gap-8">
        <form @submit.prevent="save">
            <!-- Conditions générales de vente -->
            <SectionCard :title="$t('website_settings.cguv.title')" :hideToggle="true">
                <div class="ppv2-mt-4">
                    <BaseLabel :label="$t('website_settings.cguv.label')" />
                    <BaseTextarea
                        v-model="form.general_conditions"
                        :rows="15"
                        :use-editor="true"
                        :placeholder="$t('website_settings.cguv.placeholder')"
                        :disabled="form.enabled_general_conditions"
                        :error="errors.general_conditions"
                    />
                    <BaseCheckbox
                        v-model="form.enabled_general_conditions"
                        :label="$t('website_settings.cguv.show_cguv')"
                        class="ppv2-mb-4"
                    />
                    <BaseInput
                        v-if="form.enabled_general_conditions"
                        v-model="form.general_conditions_link"
                        :label="$t('website_settings.cguv.link_label')"
                        :placeholder="$t('website_settings.cguv.link_placeholder')"
                        :required="true"
                        :error="errors.general_conditions_link"
                        class="ppv2-mt-4"
                    />
                </div>
            </SectionCard>

            <!-- Politique de confidentialité -->
            <SectionCard :title="$t('website_settings.cguv.privacy_title')" :hideToggle="true" class="ppv2-mt-8">
                <div class="ppv2-mt-4">
                    <BaseLabel :label="$t('website_settings.cguv.privacy_label')" />
                    <BaseTextarea
                        v-model="form.legal_information"
                        :rows="15"
                        :use-editor="true"
                        :placeholder="$t('website_settings.cguv.privacy_placeholder')"
                        :disabled="form.enabled_legal_information"
                        :error="errors.legal_information"
                    />
                    <BaseCheckbox
                        v-model="form.enabled_legal_information"
                        :label="$t('website_settings.cguv.show_privacy')"
                        class="ppv2-mb-4"
                    />
                    <BaseInput
                        v-if="form.enabled_legal_information"
                        v-model="form.legal_information_link"
                        :label="$t('website_settings.cguv.privacy_link_label')"
                        :placeholder="$t('website_settings.cguv.privacy_link_placeholder')"
                        :required="true"
                        :error="errors.legal_information_link"
                        class="ppv2-mt-4"
                    />
                </div>
            </SectionCard>
        </form>
    </div>
</template>

<script>
import BaseTextarea from '../../../../Shared/BaseTextarea.vue'
import BaseInput from '../../../../Shared/BaseInput.vue'
import BaseButton from '../../../../Shared/BaseButton.vue'
import BaseLabel from '../../../../Shared/BaseLabel.vue'
import BaseCheckbox from '../../../../Shared/BaseCheckbox.vue'
import SectionCard from './SectionCard.vue'

export default {
    name: 'TabCguv',
    components: {
        BaseTextarea,
        BaseInput,
        BaseButton,
        BaseLabel,
        BaseCheckbox,
        SectionCard,
    },
    props: {
        data: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            saving: false,
            errors: {},
            form: {
                general_conditions: '',
                enabled_general_conditions: false,
                general_conditions_link: '',
                legal_information: '',
                enabled_legal_information: false,
                legal_information_link: '',
            },
        }
    },
    mounted() {
        this.initializeForm()
    },
    activated() {
        this.$root.$off('website-settings-trigger-save-cguv', this.save)
        this.$root.$on('website-settings-trigger-save-cguv', this.save)
    },
    deactivated() {
        this.$root.$off('website-settings-trigger-save-cguv', this.save)
    },
    beforeDestroy() {
        this.$root.$off('website-settings-trigger-save-cguv', this.save)
    },
    methods: {
        initializeForm() {
            const settings = this.data?.settings || []

            const cgvSetting = settings.find((s) => s.parameter === 'general_conditions')
            if (cgvSetting) {
                this.form.general_conditions = cgvSetting.value || ''
            }

            const privacySetting = settings.find((s) => s.parameter === 'legal_information')
            if (privacySetting) {
                this.form.legal_information = privacySetting.value || ''
            }

            const enabledCgvSetting = settings.find((s) => s.parameter === 'enabled_general_conditions')
            if (enabledCgvSetting) {
                this.form.enabled_general_conditions = enabledCgvSetting.value === 'on'
            }

            const enabledPrivacySetting = settings.find((s) => s.parameter === 'enabled_legal_information')
            if (enabledPrivacySetting) {
                this.form.enabled_legal_information = enabledPrivacySetting.value === 'on'
            }

            const cgvLinkSetting = settings.find((s) => s.parameter === 'general_conditions_link')
            if (cgvLinkSetting) {
                this.form.general_conditions_link = cgvLinkSetting.value || ''
            }

            const privacyLinkSetting = settings.find((s) => s.parameter === 'legal_information_link')
            if (privacyLinkSetting) {
                this.form.legal_information_link = privacyLinkSetting.value || ''
            }
        },
        isValidUrl(url) {
            try {
                new URL(url)
                return true
            } catch (_) {
                return false
            }
        },
        async save() {
            this.errors = {}

            // Validation for General Conditions
            if (this.form.enabled_general_conditions) {
                if (!this.form.general_conditions_link) {
                    this.errors.general_conditions_link = this.$t('common.formRequiredError')
                } else if (!this.isValidUrl(this.form.general_conditions_link)) {
                    this.errors.general_conditions_link = this.$t('common.invalidUrlError')
                }
            }

            // Validation for Legal Information
            if (this.form.enabled_legal_information) {
                if (!this.form.legal_information_link) {
                    this.errors.legal_information_link = this.$t('common.formRequiredError')
                } else if (!this.isValidUrl(this.form.legal_information_link)) {
                    this.errors.legal_information_link = this.$t('common.invalidUrlError')
                }
            }

            if (Object.keys(this.errors).length > 0) {
                this.$toast.error(this.$t('common.error_validate'))
                return
            }

            this.saving = true
            try {
                const formData = new FormData()
                formData.append('general_conditions', this.form.general_conditions)
                formData.append('enabled_general_conditions', this.form.enabled_general_conditions ? 'on' : 'off')
                formData.append('general_conditions_link', this.form.general_conditions_link || '')
                formData.append('legal_information', this.form.legal_information)
                formData.append('enabled_legal_information', this.form.enabled_legal_information ? 'on' : 'off')
                formData.append('legal_information_link', this.form.legal_information_link || '')

                const response = await axios.post('/back-office/website/website-settings', formData)
                this.$emit('save', { loading: false, success: true, message: response.data?.message })
            } catch (error) {
                this.$toast.error(error.response.data.message)
                this.$emit('save', { loading: false })
            } finally {
                this.saving = false
            }
        },
    },
}
</script>

<style scoped></style>
